import React, { useCallback, useContext, useMemo } from "react"
import { useLocation, useParams } from "react-router-dom"
import MaterialReactTable from "material-react-table"
import { useSnackbar } from "notistack"
import { Box } from "@mui/material"
import { DateTime } from "luxon"

import useTableState from "../../Hooks/useTableState.js"
import useTableProps from "../../Hooks/useTableProps.js"
import AuthContext from "../../Context/auth_provider.jsx"
import StatusButton from "../../Component/statusButton.jsx"
import customeFilter from "../../Component/customFilterPanel.jsx"
import useUserPermissions from "../../Hooks/useUserPermissions.js"
import RestrictedAccess from "../../Component/restrictedAccess.jsx"
import CustomeDateFilter from "../../Component/customDateFilterPanel.jsx"
import usePartialUpdateEntity from "../../Hooks/usePartialUpdateEntity.js"
import useFetchEntitiesPerPage from "../../Hooks/useFetchEntitiesPerPage.js"

const users = [
    {
        accessorKey: "first_name",
        header: "Name",
        width: 200,
        renderColumnFilterModeMenuItems: customeFilter,
        Cell: ({ row }) =>
            `${row.original.first_name} ${row.original.last_name} ${row.original.middle_name}`,
    },
    {
        accessorKey: "phone",
        header: "Phone No",
        renderColumnFilterModeMenuItems: customeFilter,
        Cell: ({ cell }) => `+${cell.getValue()}`,
    },
    {
        accessorKey: "monthly_spent_amount",
        header: "Monthly Amount Spent",
        size: 250,
        filterVariant: "number",
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "total_spent_amount",
        header: "Total Amount Spent",
        size: 220,
        filterVariant: "number",
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "user_program_status",
        header: "Status",
        size: 220,
        filterVariant: "select",
        filterSelectOptions: ["ACTIVE", "INACTIVE", "PENDING"],
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "created_at",
        header: "Created At",
        filterVariant: "date",
        renderColumnFilterModeMenuItems: customeFilter,
        Filter: (props) => <CustomeDateFilter {...props} />,
        Cell: ({ cell }) =>
            `${DateTime.fromISO(cell.getValue()).toFormat("ff")}`,
    },
]

function PreRIDEProgramUsers() {
    const {
        viewPreRideUsers,
        getTeamPreRideUsers,
        updateTeamUserPreRideStatus,
    } = useUserPermissions()
    const {
        params,
        state: tableState,
        onChangeHandlers,
    } = useTableState({
        columns: users,
    })

    // SPECIFIC PROGRAM ID, COMES FROM THE PROGRAMS TABLE
    const { state } = useLocation()
    const pId = state.id

    const { tId } = useParams()
    const { teamId } = useContext(AuthContext)
    const patchEndPoint = tId ? `team/${tId}/preride/users` : `preride/users`

    // HOOK TO UPDATE PROGRAMS, USED FOR STATUS CHANGE HERE
    const { mutate, isLoading: updating } =
        usePartialUpdateEntity(patchEndPoint)

    const { enqueueSnackbar } = useSnackbar()

    const { data, isLoading, isFetching, refetch } = useFetchEntitiesPerPage(
        {
            endPoint: tId
                ? `teams/${tId}/preride/${pId}/users`
                : `preride/${pId}/users`,
            ...params,
        },
        { enabled: tId ? getTeamPreRideUsers : viewPreRideUsers }
    )

    // HANDLERS update team user preride status"
    const handleStatusChange = useCallback(
        (checked, row) => {
            mutate(
                {
                    id: row?.id,
                    preride_program_id: pId,
                    status: checked ? "ACTIVE" : "INACTIVE",
                },
                {
                    onError: (error) => {
                        enqueueSnackbar(
                            `status update failed due to ${error.message}`,
                            { variant: "error" }
                        )
                    },
                    onSuccess: () => {
                        enqueueSnackbar("status updated successfully", {
                            variant: "success",
                        })
                        refetch()
                    },
                }
            )
        },
        [enqueueSnackbar, mutate, pId, refetch]
    )

    const actionColumn = useCallback(
        ({ cell, row }) => (
            <StatusButton
                loading={updating}
                disabled={
                    // eslint-disable-next-line no-nested-ternary
                    tId
                        ? !updateTeamUserPreRideStatus
                        : teamId
                        ? updateTeamUserPreRideStatus
                        : false
                }
                status={cell.getValue() === "ACTIVE" ? "ACTIVE" : "INACTIVE"}
                onChange={(checked) =>
                    handleStatusChange(checked, row.original)
                }
            />
        ),
        [handleStatusChange, tId, teamId, updateTeamUserPreRideStatus, updating]
    )

    const colFields = useMemo(
        () =>
            users?.map((ele) => {
                if (ele.accessorKey === "user_program_status")
                    return {
                        ...ele,
                        Cell: actionColumn,
                    }
                return ele
            }),
        [actionColumn]
    )

    const { props } = useTableProps({
        columns: colFields,
        exportProps: {
            dataEndPoints: `programs/${pId}/users`,
            model: params.filterModel,
        },
        state: {
            ...tableState,
            showSkeletons: isLoading,
            showProgressBars: isFetching,
        },
        refetch: refetch,
    })

    if (tId ? !getTeamPreRideUsers : !viewPreRideUsers)
        return <RestrictedAccess />

    return (
        <Box sx={{ backgroundColor: "white" }}>
            <MaterialReactTable
                data={data?.data?.data || []}
                rowCount={data?.data?.meta_data?.total ?? 0}
                initialState={{
                    columnPinning: { right: ["status"] },
                }}
                {...onChangeHandlers}
                {...props}
            />
        </Box>
    )
}

export default PreRIDEProgramUsers
